import React, { useContext, useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { MenuLayer } from 'components/menu'

const WIDTH = 280
const GAP = 6
const OPEN_DELAY = 300
const CLOSE_DELAY = 150
// Room a card needs under its trigger; with less, as at the foot of a panel, it opens above it instead.
const ROOM_BELOW = 160

/**
 * A card that opens over something pointed at, in the manner of shadcn/ui's hover card: a title and a few
 * lines saying more about it, under it (or over it, without room below) and lined up with its start. It stays open while the pointer moves
 * onto it. The trigger is underlined with dots, so it reads as something to point at. As in shadcn/ui, it's
 * for the pointer: what it says adds to what's shown, which stands without it.
 */
const HoverCard: React.FC<{
  title: React.ReactNode
  children: React.ReactNode
  content: React.ReactNode
  /** For the trigger, as when it lays out what it wraps: a row of an icon and text that truncates. */
  className?: string
  /** Without the dotted underline: for a trigger that already reads as something to point at, or one of
   * several lines where the dots would be too much. */
  plain?: boolean
}> = ({ title, children, content, className, plain }) => {
  const id = useId()
  // In a dialog, its layer for menus, so the card counts as inside it and can be pointed at.
  const layer = useContext(MenuLayer)
  const [trigger, setTrigger] = useState<HTMLElement | null>(null)
  const [hovering, setHovering] = useState(false)
  const [at, setAt] = useState<{ top?: number; bottom?: number; left: number } | null>(null)

  // Opens a moment after the pointer arrives, and closes a moment after it leaves both the trigger and the
  // card, so passing over a row doesn't flash cards and moving onto the card doesn't close it.
  useEffect(() => {
    if (hovering && trigger) {
      const timer = window.setTimeout(() => {
        const rect = trigger.getBoundingClientRect()
        const left = Math.min(rect.left, window.innerWidth - WIDTH - 8)
        setAt(
          window.innerHeight - rect.bottom >= ROOM_BELOW
            ? { top: rect.bottom + GAP, left }
            : { bottom: window.innerHeight - rect.top + GAP, left }
        )
      }, OPEN_DELAY)
      return () => window.clearTimeout(timer)
    }
    const timer = window.setTimeout(() => setAt(null), CLOSE_DELAY)
    return () => window.clearTimeout(timer)
  }, [hovering, trigger])

  const hover = {
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false)
  }

  return (
    <>
      <span
        ref={setTrigger}
        aria-describedby={at ? id : undefined}
        className={`cursor-default ${plain ? '' : 'underline decoration-dotted decoration-faint underline-offset-4'} ${className ?? ''}`}
        {...hover}
      >
        {children}
      </span>
      {at &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            className="fixed z-[4000] flex flex-col gap-1 rounded-lg border border-divider bg-surface-raised p-3 text-[13px] whitespace-normal shadow-[0_4px_12px_-2px_rgb(0_0_0/0.12),0_2px_4px_-2px_rgb(0_0_0/0.08)] animate-menu-in motion-reduce:animate-none"
            style={{ top: at.top, bottom: at.bottom, left: at.left, width: WIDTH }}
            {...hover}
          >
            <div className="font-medium text-body">{title}</div>
            <div className="text-muted">{content}</div>
          </div>,
          layer || document.body
        )}
    </>
  )
}

export default HoverCard
